import React, { useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Divider,
  Tabs,
  Tab,
  Button,
  Modal,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Link,
} from '@mui/material';
import dayjs from 'dayjs';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import _ from 'lodash';
import { EXP_STATUS_MAP } from '../../../../utils/constants/financeStatus.constant';
import { BudgetForm } from '../../../../components/Forms';
import { CSVUploadModal } from '../../../../components/Modal';
import BudgetListTable from '../../../../components/Table/BudgetListTable';
import { showAlert, confirmAsync } from '../../../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../../../utils/functions/employeeName';
import ProjectSandboxBudgetTable from './ProjectSandboxBudgetTable';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 1000,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const genLocalId = () => `sbx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// clone ของแท็บ "รายละเอียด" (sheet===1) ใน ProjectPopup.js — ทั้ง 5 แท็บย่อย (แดชบอร์ด/งบประมาณ/
// รายการเบิก/ลงเวลา/เอกสาร) ทำหน้าที่เหมือนต้นฉบับ เพียงแต่ทุกการแก้ไขทำงานกับ p.budget (ผ่าน
// applyPatch ของ sandbox) ในเครื่องล้วนๆ ไม่มี dispatch/api สักจุด — ไม่มีปุ่มเลื่อนก่อนหน้า/ถัดไป
// (ไม่มีความหมายเพราะ sandbox มีแค่โปรเจกต์เดียว) ตามที่ตกลงไว้
export function ProjectSandboxDetailTab({ project: p, applyPatch, uiLang = 'en' }) {
  const L = (th, en) => (uiLang === 'en' ? en : th);
  const [tab, setTab] = useState(0);
  const [openBudget, setOpenBudget] = useState(false);
  const [openEditBudget, setOpenEditBudget] = useState(false);
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [expenseDialog, setExpenseDialog] = useState(null);
  const budgetTableRef = useRef(null);
  const [budgetToggleState, setBudgetToggleState] = useState({ allExpanded: false, disabled: true });
  const { control, handleSubmit, reset, setValue } = useForm();

  const budget = p?.budget || [];
  const expenses = p?.expenses || [];

  const nextBudgetNumber = () => {
    const maxNum = budget.reduce((m, b) => {
      const n = parseInt(String(b?.budget_number || '').replace(/\D/g, ''), 10);
      return Number.isFinite(n) ? Math.max(m, n) : m;
    }, 0);
    return String(maxNum + 1).padStart(4, '0');
  };

  const updateBudgetItem = (updated) => {
    const id = updated?.id || updated?._id;
    applyPatch({
      budget: budget.map((b) => ((b.id || b._id) === id ? updated : b)),
    });
  };

  const onSubmitBudget = async (data) => {
    if (!(await confirmAsync('บันทึก'))) return;
    const newId = genLocalId();
    const newItem = {
      _id: newId,
      id: newId,
      prefix: budget[0]?.prefix || '',
      budget_number: nextBudgetNumber(),
      name: data.name,
      cost: Number(data.cost) || 0,
      expenses: [],
      pending_items: [],
      refund_items: [],
    };
    applyPatch({ budget: [...budget, newItem] });
    reset();
    setOpenBudget(false);
  };

  const onSubmitEditBudget = async (data) => {
    if (!(await confirmAsync('บันทึก'))) return;
    const target = budget.find((b) => (b.id || b._id) === data.id);
    if (!target) return;
    updateBudgetItem({ ...target, name: data.name, cost: Number(data.cost) || 0 });
    reset();
    setOpenEditBudget(false);
  };

  const handleEditBudget = (data) => {
    setOpenEditBudget(true);
    setValue('budget_number', data?.budget_number);
    setValue('name', data?.name);
    setValue('cost', data?.cost);
    setValue('id', data?.id || data?._id);
  };

  const handleDeleteBudget = async (item) => {
    if (!_.isEmpty(item?.expenses)) {
      return showAlert('ไม่สามารถลบงบประมาณที่มีรายการค่าใช้จ่ายผูกอยู่ได้');
    }
    if (await confirmAsync('ลบงบประมาณนี้?')) {
      const id = item?.id || item?._id;
      applyPatch({ budget: budget.filter((b) => (b.id || b._id) !== id) });
    }
  };

  // นำเข้า CSV — CSVUpload (react-papaparse) parse ในเครื่องล้วนๆ อยู่แล้ว (ไม่อัปโหลดไปไหน) จึงใช้
  // CSVUploadModal ตัวจริงได้ตรงๆ ปลอดภัย แค่ handler ปลายทางนี้ (handleAddFromFile) merge เข้า
  // p.budget ในเครื่องแทนที่จะ dispatch(actions.budgetCreate) เหมือนของจริง
  const handleAddFromFile = () => {
    if (_.isEmpty(csvData)) return showAlert('ไม่สามารถอ่านไฟล์รายการได้');
    let n = nextBudgetNumber();
    const newItems = csvData.map((row) => {
      const num = String(n).padStart(4, '0');
      n = Number(n) + 1;
      const id = genLocalId();
      return {
        _id: id,
        id,
        prefix: budget[0]?.prefix || '',
        budget_number: row.budget_number || num,
        name: row.name || row['รายการ'] || '',
        cost: Number(row.cost || row['จำนวนเงิน']) || 0,
        expenses: [],
        pending_items: [],
        refund_items: [],
      };
    });
    applyPatch({ budget: [...budget, ...newItems] });
    showAlert('สำเร็จ');
    setCsvUploadOpen(false);
    setCsvData([]);
  };

  const sortedExpenses = [...expenses].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <Box sx={{ p: 2.5, overflowX: 'hidden' }}>
      <Divider sx={{ mb: 1 }} />

      <Stack direction="row" alignItems="center" sx={{ position: 'relative' }}>
        <Tabs
          value={tab}
          onChange={(_ev, v) => setTab(v)}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, fontSize: '0.8rem', py: 0.5 },
          }}
        >
          <Tab label={L('แดชบอร์ด', 'Dashboard')} />
          <Tab label={L('งบประมาณ', 'Budget')} />
          <Tab label={L('รายการเบิก', 'Expenses')} />
          <Tab label={L('ลงเวลา', 'Time')} />
          <Tab label={L('เอกสาร', 'Documents')} />
        </Tabs>
        <Box sx={{ flexGrow: 1 }} />
        {tab === 0 && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => budgetTableRef.current?.toggleAllRows()}
            startIcon={budgetToggleState.allExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            disabled={budgetToggleState.disabled}
          >
            {budgetToggleState.allExpanded ? L('ปิดทั้งหมด', 'Collapse all') : L('ดูค่าใช้จ่ายทั้งหมด', 'View all expenses')}
          </Button>
        )}
      </Stack>

      <Box sx={{ pt: 1.5 }}>
        {/* แดชบอร์ด */}
        {tab === 0 && (
          <ProjectSandboxBudgetTable
            ref={budgetTableRef}
            budget={budget}
            handleEditBudget={handleEditBudget}
            handleDeleteBudget={handleDeleteBudget}
            onEditExpense={(expenseId) =>
              setExpenseDialog(expenses.find((e) => (e._id || e.id) === expenseId) || null)
            }
            onBudgetChange={updateBudgetItem}
            hideToggleButton
            onToggleStateChange={setBudgetToggleState}
            uiLang={uiLang}
          />
        )}

        {/* งบประมาณ */}
        {tab === 1 && (
          <Box>
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 1 }}>
              <Button size="small" variant="contained" onClick={() => setOpenBudget(true)}>
                {L('เพิ่มงบประมาณ', 'Add budget')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<UploadFileIcon />}
                onClick={() => setCsvUploadOpen(true)}
              >
                {L('นำเข้าข้อมูล', 'Import')}
              </Button>
            </Stack>
            <BudgetListTable
              budget={budget}
              handleEditBudget={handleEditBudget}
              handleDeleteBudget={handleDeleteBudget}
              uiLang={uiLang}
            />
          </Box>
        )}

        {/* รายการเบิก — ดึงจาก p.expenses ที่ clone มาแล้วตรงๆ (ไม่ fetch ซ้ำเหมือนของจริง) */}
        {tab === 2 && (
          <Box>
            {sortedExpenses.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                {L('ยังไม่มีรายการเบิก', 'No expenses')}
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {[
                        L('วันที่', 'Date'),
                        L('รหัส', 'Code'),
                        L('รายการ', 'Item'),
                        L('หมวด', 'Category'),
                        L('ผู้เบิก', 'Requester'),
                        L('ผู้รับเงิน', 'Payee'),
                        L('จำนวน', 'Amount'),
                        L('สถานะ', 'Status'),
                      ].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            bgcolor: '#3730a3',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            whiteSpace: 'nowrap',
                            py: 0.75,
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedExpenses.map((exp, i) => {
                      const st = EXP_STATUS_MAP[exp.status] || EXP_STATUS_MAP.PENDING;
                      const empName = exp.employee ? formatEmployeeThaiName(exp.employee) : '-';
                      const budgetName = budget.find(
                        (b) => String(b._id || b.id) === String(exp.budget),
                      )?.name || exp.budget?.name || '-';
                      return (
                        <TableRow
                          key={exp._id || exp.id || i}
                          onClick={() => setExpenseDialog(exp)}
                          sx={{
                            bgcolor: i % 2 === 0 ? '#fff' : '#f8f8fc',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#eff6ff' },
                          }}
                        >
                          <TableCell sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                            {exp.date ? dayjs(exp.date).format('DD/MM/BB') : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap', color: '#64748b' }}>
                            {exp.code || '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.72rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {exp.name || '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.72rem', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#64748b' }}>
                            {budgetName}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{empName}</TableCell>
                          <TableCell sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap', color: '#64748b' }}>
                            {exp.payee?.name || '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap', textAlign: 'right', fontWeight: 600 }}>
                            {Number(exp.price || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell sx={{ py: 0.4 }}>
                            <Chip
                              label={L(st.label, {
                                PENDING: 'Pending',
                                PREAPPROVE: 'Reapproval',
                                PREPARE: 'Preparing',
                                APPROVE: 'Approved',
                                SUCCESS: 'Completed',
                                REJECT: 'Rejected',
                                CANCEL: 'Cancelled',
                              }[exp.status] || st.label)}
                              size="small"
                              sx={{ bgcolor: st.bg, color: st.color, fontWeight: 700, fontSize: '0.65rem', height: 18 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* ลงเวลา — ของจริงดึงจาก timestamp collection แยกต่างหากผ่าน dispatch(actions.timestampAll(...))
            ไม่ได้ฝังอยู่ใน document โปรเจกต์เอง (formulas.js ก็ไม่เคยอ่าน row.timestamp เลย) จึงไม่มี
            ข้อมูลให้ clone มาเล่นได้จริงใน sandbox — โชว์ placeholder ตรงไปตรงมาแทนที่จะปลอมข้อมูลขึ้นมาเอง */}
        {tab === 3 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {L(
              'ยังไม่รองรับข้อมูลลงเวลาในโหมดทดสอบ (ข้อมูลลงเวลาไม่ได้ผูกอยู่กับโปรเจกต์ที่ clone มา)',
              'Time-tracking records are not simulated in sandbox mode (not part of the cloned project data).',
            )}
          </Typography>
        )}

        {/* เอกสาร — อ่านอย่างเดียวเหมือนต้นฉบับ (google_drive_url มาจาก LINE OA แก้ไม่ได้อยู่แล้วแม้แต่ใน
            หน้าจริง, documents[] เพิ่มผ่านฟอร์มแก้ไขโครงการซึ่งอยู่นอกขอบเขต sandbox) */}
        {tab === 4 && (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
                <LockOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  {L('ลิงก์ Google Drive จาก LINE OA (แก้ไข/ลบเองไม่ได้)', 'Google Drive link from LINE OA (read-only)')}
                </Typography>
              </Stack>
              {p?.google_drive_url ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                  <OpenInNewIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                  <Link
                    href={p.google_drive_url}
                    target="_blank"
                    rel="noopener"
                    sx={{ fontSize: '0.8rem', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {p.google_drive_url}
                  </Link>
                </Box>
              ) : (
                <Typography variant="body2" color="text.disabled">
                  {L('ยังไม่มีลิงก์จาก LINE OA', 'No link from LINE OA yet')}
                </Typography>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              {L('เอกสารแนบ', 'Attached documents')}
            </Typography>
            {p?.documents?.length > 0 ? (
              <Stack spacing={0.5}>
                {p.documents.map((doc, i) => (
                  <Box
                    key={i}
                    onClick={() => window.open(doc.url, '_blank')}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <OpenInNewIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {doc.title || `${L('เอกสาร', 'Document')} ${i + 1}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {doc.url}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                {L('ยังไม่มีเอกสาร', 'No documents')}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Add budget */}
      <Modal open={openBudget} onClose={() => setOpenBudget(false)}>
        <Box sx={modalStyle}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>{L('เพิ่มงบประมาณ', 'Add budget')}</Typography>
            <Tooltip title={L('เลขนี้จำลองในเครื่อง ไม่ผูกกับระบบจริง', 'This number is generated locally, not from the real system')} arrow>
              <Chip
                label={nextBudgetNumber()}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 700, fontFamily: 'monospace' }}
              />
            </Tooltip>
          </Box>
          <form onSubmit={handleSubmit(onSubmitBudget)}>
            <BudgetForm Controller={Controller} control={control} />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="contained" type="submit" size="small">บันทึก</Button>
            </Box>
          </form>
        </Box>
      </Modal>

      {/* Edit budget */}
      <Modal open={openEditBudget} onClose={() => setOpenEditBudget(false)}>
        <Box sx={modalStyle}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>{L('แก้ไขงบประมาณ', 'Edit budget')}</Typography>
          <form onSubmit={handleSubmit(onSubmitEditBudget)}>
            <BudgetForm Controller={Controller} control={control} showNumber />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="contained" type="submit" size="small">บันทึก</Button>
            </Box>
          </form>
        </Box>
      </Modal>

      <CSVUploadModal
        csvData={csvData}
        setCsvData={setCsvData}
        fileTemplateURL="/filetemplate/budget_template.csv"
        handleAddFromFile={handleAddFromFile}
        handleClose={() => { setCsvUploadOpen(false); setCsvData([]); }}
        isOpen={csvUploadOpen}
        titleName="รายการงบประมาณจากไฟล์ CSV (ทดสอบ)"
        header={0}
        anotherComponent={<div />}
      />

      {/* รายละเอียดรายการเบิก — แทนที่ ExpensesDetail จริง (ผูก redux/API ลึกเกินขอบเขตของ preview)
          ด้วย dialog อ่านอย่างเดียวจากข้อมูล clone ตรงๆ */}
      <Dialog open={!!expenseDialog} onClose={() => setExpenseDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{L('รายละเอียดรายการเบิก (ทดสอบ)', 'Expense detail (sandbox)')}</DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          {expenseDialog && (
            <Stack spacing={1} sx={{ pt: 1 }}>
              <Row2 label={L('วันที่', 'Date')} value={expenseDialog.date ? dayjs(expenseDialog.date).format('DD/MM/YYYY') : '-'} />
              <Row2 label={L('รหัส', 'Code')} value={expenseDialog.code || '-'} />
              <Row2 label={L('รายการ', 'Item')} value={expenseDialog.name || '-'} />
              <Row2 label={L('จำนวน', 'Amount')} value={Number(expenseDialog.price || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} />
              <Row2 label={L('ผู้รับเงิน', 'Payee')} value={expenseDialog.payee?.name || '-'} />
              <Row2 label={L('สถานะ', 'Status')} value={(EXP_STATUS_MAP[expenseDialog.status] || EXP_STATUS_MAP.PENDING).label} />
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function Row2({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ fontSize: '0.82rem' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Stack>
  );
}
